/**
 * Small collection of reusable UI helpers for the admin dashboard.
 */
import { NavLink } from "react-router-dom";

export function PageHeader({ title, description, actions }) {
    return (
        <header className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
            <div>
                <h1 className="text-2xl font-semibold tracking-tight" data-testid="page-title">
                    {title}
                </h1>
                {description && (
                    <p className="text-sm text-slate-500 mt-1 max-w-xl">{description}</p>
                )}
            </div>
            {actions && <div className="flex flex-wrap gap-2">{actions}</div>}
        </header>
    );
}

export function StatCard({ label, value, testId }) {
    return (
        <div
            className="bg-white border border-slate-200 rounded-xl p-5"
            data-testid={testId}
        >
            <div className="text-[10px] tracking-[0.24em] uppercase text-slate-500">
                {label}
            </div>
            <div className="text-2xl md:text-3xl font-semibold mt-1">{value ?? "—"}</div>
        </div>
    );
}

export function Card({ children, className = "", ...rest }) {
    return (
        <div
            className={`bg-white border border-slate-200 rounded-xl ${className}`}
            {...rest}
        >
            {children}
        </div>
    );
}

export function Button({ children, variant = "primary", className = "", ...rest }) {
    const base =
        "inline-flex items-center gap-2 rounded-lg px-3.5 py-2 text-sm font-medium transition disabled:opacity-50 disabled:cursor-not-allowed";
    const variants = {
        primary: "bg-slate-900 text-white hover:bg-slate-800",
        secondary: "bg-white border border-slate-300 text-slate-800 hover:bg-slate-50",
        ghost: "text-slate-700 hover:bg-slate-100",
        danger: "bg-red-600 text-white hover:bg-red-500",
    };
    return (
        <button className={`${base} ${variants[variant]} ${className}`} {...rest}>
            {children}
        </button>
    );
}

export function Field({ label, hint, children }) {
    return (
        <label className="block">
            <div className="text-xs font-medium text-slate-600 mb-1">{label}</div>
            {children}
            {hint && <div className="text-[11px] text-slate-500 mt-1">{hint}</div>}
        </label>
    );
}

export function Input(props) {
    return (
        <input
            {...props}
            className={
                "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-800/10 " +
                (props.className || "")
            }
        />
    );
}

export function TextArea(props) {
    return (
        <textarea
            {...props}
            className={
                "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-800/10 min-h-[80px] " +
                (props.className || "")
            }
        />
    );
}

export function Select(props) {
    return (
        <select
            {...props}
            className={
                "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-slate-800/10 " +
                (props.className || "")
            }
        />
    );
}

export function Badge({ children, tone = "neutral" }) {
    const tones = {
        neutral: "bg-slate-100 text-slate-700",
        success: "bg-emerald-100 text-emerald-700",
        warning: "bg-amber-100 text-amber-800",
        danger: "bg-red-100 text-red-700",
    };
    return (
        <span className={`inline-flex text-[11px] font-medium tracking-wide px-2 py-0.5 rounded-full ${tones[tone]}`}>
            {children}
        </span>
    );
}

export function EmptyState({ title, description, action }) {
    return (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
            <div className="text-slate-800 font-medium mb-1">{title}</div>
            {description && <p className="text-sm text-slate-500 max-w-md mx-auto mb-4">{description}</p>}
            {action}
        </div>
    );
}

export function BackLink({ to, label = "Indietro" }) {
    return (
        <NavLink to={to} className="text-xs text-slate-500 hover:text-slate-900 mb-3 inline-block">
            ← {label}
        </NavLink>
    );
}

export function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("it-IT", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch {
        return iso;
    }
}
